/**
 * Notifications inbox — the list body of the /notifications page's Inbox tab.
 *
 * Presentational: the page (`pages/Notifications.tsx`) owns the sticky
 * PageActionBar (tabs + filter + bulk actions) and the inbox data hooks, and
 * hands this component the resolved `inbox` query + `actions`. Here we render
 * only the day-grouped list and its loading / empty / error states, full-width
 * in a single bordered container that mirrors OrdersList's table container so
 * every row shares the same left/right edge (DESIGN.md §14).
 */
import { Fragment, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, isToday, isYesterday } from 'date-fns';
import { AlertCircle, BellOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { DATE_FORMAT, useDateFnsLocale } from '@/lib/format-dates';
import { NotificationRow } from './NotificationRow';
import type {
  useNotificationActions,
  useNotificationInbox,
} from './useNotifications';
import type { NotificationFilter, NotificationItem } from './notifications-procedures';

interface DayGroup {
  key: string;
  label: string;
  items: NotificationItem[];
}

interface NotificationInboxProps {
  filter: NotificationFilter;
  onFilterChange: (filter: NotificationFilter) => void;
  inbox: ReturnType<typeof useNotificationInbox>;
  actions: ReturnType<typeof useNotificationActions>;
}

export function NotificationInbox({
  filter,
  onFilterChange,
  inbox,
  actions,
}: NotificationInboxProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = useDateFnsLocale();

  const { markRead, dismiss } = actions;
  const { items, isLoading, isError, hasNextPage, isFetchingNextPage } = inbox;

  const groups = useMemo<DayGroup[]>(() => {
    const map = new Map<string, DayGroup>();
    for (const item of items) {
      const date = new Date(item.created_at);
      const key = format(date, 'yyyy-MM-dd');
      let group = map.get(key);
      if (!group) {
        const label = isToday(date)
          ? t('notifications.group.today', { defaultValue: 'Today' })
          : isYesterday(date)
            ? t('notifications.group.yesterday', { defaultValue: 'Yesterday' })
            : format(date, DATE_FORMAT, { locale });
        group = { key, label, items: [] };
        map.set(key, group);
      }
      group.items.push(item);
    }
    return Array.from(map.values());
  }, [items, t, locale]);

  const handleSelect = (item: NotificationItem) => {
    if (!item.read_at) markRead.mutate({ ids: [item.id] });
    if (item.link) navigate(item.link);
  };

  if (isLoading) return <InboxSkeleton />;
  if (isError) return <InboxError onRetry={() => inbox.refetch()} />;
  if (items.length === 0) return <InboxEmpty filter={filter} onSeeAll={() => onFilterChange('all')} />;

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
        {groups.map((group, gi) => (
          <Fragment key={group.key}>
            {/* Quiet day-group band — mirrors the grouped-table header (§14.1). */}
            <div
              className={cn(
                'flex items-baseline gap-2 bg-muted/40 px-4 py-2',
                gi > 0 && 'border-t border-border',
              )}
            >
              <h2 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {group.label}
              </h2>
              <span className="text-[11px] tabular-nums text-muted-foreground/60">
                {group.items.length}
              </span>
            </div>
            {group.items.map((item) => (
              <div key={item.id} className="border-t border-border/60">
                <NotificationRow
                  item={item}
                  variant="inbox"
                  onSelect={handleSelect}
                  onDismiss={(it) => dismiss.mutate({ id: it.id })}
                  onMarkRead={(it) => markRead.mutate({ ids: [it.id] })}
                  markReadLabel={t('notifications.markRead', { defaultValue: 'Mark as read' })}
                  dismissLabel={t('notifications.dismiss', { defaultValue: 'Dismiss' })}
                />
              </div>
            ))}
          </Fragment>
        ))}
      </div>

      {hasNextPage && (
        <div className="flex justify-center pt-1">
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 px-4 text-xs"
            disabled={isFetchingNextPage}
            onClick={() => inbox.fetchNextPage()}
          >
            {isFetchingNextPage && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t('notifications.loadMore', { defaultValue: 'Load more' })}
          </Button>
        </div>
      )}
    </div>
  );
}

function InboxSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border bg-card shadow-sm" aria-hidden="true">
      <div className="bg-muted/40 px-4 py-2">
        <Skeleton className="h-3 w-16" />
      </div>
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className={cn('flex items-start gap-3 px-4 py-3', i > 0 && 'border-t border-border/60')}
        >
          <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3.5 w-3/5" />
            <Skeleton className="h-3 w-2/5" />
          </div>
          <Skeleton className="h-3 w-12 shrink-0" />
        </div>
      ))}
    </div>
  );
}

function InboxError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border bg-muted/20 px-6 py-20 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600">
        <AlertCircle className="h-6 w-6" />
      </span>
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">
          {t('notifications.error.title', { defaultValue: "Couldn't load notifications" })}
        </p>
        <p className="mx-auto max-w-sm text-sm text-muted-foreground">
          {t('notifications.error.subtitle', {
            defaultValue: 'Something went wrong while fetching your inbox. Try again.',
          })}
        </p>
      </div>
      <Button variant="outline" size="sm" className="mt-1 h-8 px-3 text-xs" onClick={onRetry}>
        {t('notifications.error.retry', { defaultValue: 'Try again' })}
      </Button>
    </div>
  );
}

function InboxEmpty({
  filter,
  onSeeAll,
}: {
  filter: NotificationFilter;
  onSeeAll: () => void;
}) {
  const { t } = useTranslation();
  const isUnread = filter === 'unread';
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border bg-muted/20 px-6 py-20 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <BellOff className="h-6 w-6" />
      </span>
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">
          {isUnread
            ? t('notifications.inbox.emptyUnreadTitle', { defaultValue: 'No unread notifications' })
            : t('notifications.inbox.emptyAllTitle', { defaultValue: 'No notifications yet' })}
        </p>
        <p className="mx-auto max-w-sm text-sm text-muted-foreground">
          {isUnread
            ? t('notifications.inbox.emptyUnreadSubtitle', {
                defaultValue: "You've read everything. Switch to All to see your history.",
              })
            : t('notifications.inbox.emptyAllSubtitle', {
                defaultValue: 'Mentions, assignments, and system alerts will appear here.',
              })}
        </p>
      </div>
      {isUnread && (
        <Button variant="ghost" size="sm" className="mt-1 h-8 px-3 text-xs" onClick={onSeeAll}>
          {t('notifications.inbox.viewAllHistory', { defaultValue: 'View all' })}
        </Button>
      )}
    </div>
  );
}
